import { isAudioStation } from '@app/core/stations/helpers';
import { useStationsFinder } from '@app/core/stations/hooks/useStations';
import { SOCIAL_EXPORT_FRAGMENT_SOURCE } from '@app/modules/social-export/lib/social-export-fragment-source';
import { CreateSocialUploadDtoSourceType } from '@app/modules/fragments/fragment.types';
import {
  getVideoEditionsEditListRefetchInterval,
  isVideoEditorEditSocialPublishable,
} from '@app/modules/video-editions';
import { useWebAppListEditsVideoEditorEdits } from '@mi-front/api-v2';
import { useMemo } from 'react';

export { SOCIAL_EXPORT_FRAGMENT_SOURCE };

export interface SocialExportAvailableSource {
  value: string;
  sourceId: string;
  sourceType: (typeof CreateSocialUploadDtoSourceType)[keyof typeof CreateSocialUploadDtoSourceType];
}

interface UseAvailableSocialExportSourcesParams {
  fragmentId: string;
  stationId?: string;
}

/**
 * Resolves publishable sources for social export for a fragment.
 * Audio stations only allow video-edition edits that have a render in progress or completed.
 * Fragment original media is available for video stations even before assets exist.
 */
export function useAvailableSocialExportSources({
  fragmentId,
  stationId,
}: UseAvailableSocialExportSourcesParams) {
  const findStation = useStationsFinder();
  const station = stationId ? findStation(stationId) : null;
  const isFragmentSourceAllowed = station ? !isAudioStation(station) : true;

  const editsListQuery = useWebAppListEditsVideoEditorEdits(
    { fragmentId },
    {
      query: {
        enabled: Boolean(fragmentId),
        refetchInterval: (query) => getVideoEditionsEditListRefetchInterval(query.state.data),
      },
    },
  );

  const edits = editsListQuery.data ?? [];
  const publishableEdits = useMemo(() => edits.filter(isVideoEditorEditSocialPublishable), [edits]);

  const availableSources = useMemo(() => {
    const fragmentSource: SocialExportAvailableSource[] = isFragmentSourceAllowed
      ? [
          {
            value: SOCIAL_EXPORT_FRAGMENT_SOURCE,
            sourceId: fragmentId,
            sourceType: CreateSocialUploadDtoSourceType.FRAGMENT,
          },
        ]
      : [];
    const editSources = publishableEdits.map(
      (edit): SocialExportAvailableSource => ({
        value: edit.id,
        sourceId: edit.id,
        sourceType: CreateSocialUploadDtoSourceType.VIDEO_EDITOR_EDIT,
      }),
    );

    return [...fragmentSource, ...editSources];
  }, [fragmentId, isFragmentSourceAllowed, publishableEdits]);

  return {
    editsListQuery,
    edits,
    publishableEdits,
    isFragmentSourceAllowed,
    availableSources,
    hasAvailableSources: availableSources.length > 0,
  };
}
